import { useState, type ReactNode } from "react";
// React type for cell-getValue casting below.
import type { ReactNode as RN } from "react";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Search,
} from "lucide-react";

/**
 * AdminTable — shared headless table for every admin surface
 * (ROADS-478). Wraps `@tanstack/react-table` so each surface only
 * has to define its `columns` shape; sorting + filtering +
 * pagination + empty state are inherited.
 *
 * Why headless: the visual chrome (indigo accents, slate ink hierarchy)
 * is too project-specific for off-the-shelf markup, but the state
 * machine (sort direction, page index, filter string) is the part
 * that's worth not reinventing. TanStack Table gives us the state +
 * helpers; we render the markup ourselves.
 *
 * Phase-1 scope: client-side sorting + filtering + pagination only.
 * Server-side variants land when the API integration ticket lands
 * (ROADS-476 deliverable A1) — TanStack Table supports the manual
 * pagination/sorting mode that hands the work to the server.
 */
export interface AdminTableProps<T> {
  /** Row data. */
  data: T[];
  /** Column definitions from `@tanstack/react-table`. */
  columns: ColumnDef<T, unknown>[];
  /**
   * Optional global-filter UI above the table. Searches every column.
   * Per-column filters are available via column-def `filterFn`.
   */
  enableGlobalFilter?: boolean;
  globalFilterPlaceholder?: string;
  /** Default 10. */
  pageSize?: number;
  /**
   * Whether the AdminTable owns its pagination. Default true (client-side).
   * Pass `false` when the caller paginates server-side and supplies its own
   * pagination chrome below the table — AdminTable then renders the full
   * `data` array without slicing and omits the pagination footer.
   */
  enablePagination?: boolean;
  /** Custom empty-state node; rendered in place of the data rows. */
  emptyState?: ReactNode;
  /** Test id forwarded to the outer table container. */
  testId?: string;
  /**
   * Optional per-row testid. Lets callers preserve a stable
   * `data-testid="prefix-${id}"` contract on each `<tr>` (e.g.
   * `admin-question-row-${q.id}` from QuestionListTable's earlier
   * pre-AdminTable contract). Defaults to `${testId}-row-${row.id}`.
   */
  rowTestId?: (row: T) => string;
}

export default function AdminTable<T>({
  data,
  columns,
  enableGlobalFilter = false,
  globalFilterPlaceholder = "Filter…",
  pageSize = 10,
  enablePagination = true,
  emptyState,
  testId,
  rowTestId,
}: AdminTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize } },
        }
      : {}),
  });

  const total = table.getFilteredRowModel().rows.length;
  const pageRows = table.getRowModel().rows;
  const pageIndex = enablePagination
    ? table.getState().pagination.pageIndex
    : 0;
  const startIdx = total === 0 ? 0 : pageIndex * pageSize + 1;
  const endIdx = enablePagination
    ? Math.min(total, (pageIndex + 1) * pageSize)
    : total;
  const totalPages = Math.max(1, table.getPageCount());

  return (
    <div className="space-y-3">
      {enableGlobalFilter && (
        <label className="flex w-full max-w-[360px] items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5">
          <Search aria-hidden="true" className="h-3.5 w-3.5 text-slate-500" />
          <input
            type="search"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={globalFilterPlaceholder}
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            data-testid={testId ? `${testId}-filter` : undefined}
          />
        </label>
      )}
      <div
        className="overflow-hidden rounded-xl border border-slate-200 bg-white"
        data-testid={testId}
      >
        <table className="w-full border-collapse text-sm" role="table">
          <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-slate-200">
                {hg.headers.map((header) => {
                  const sortable =
                    !header.isPlaceholder && header.column.getCanSort();
                  const sort = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className="px-5 py-3 text-left font-bold"
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                          aria-label={`Sort by ${typeof header.column.columnDef.header === "string" ? header.column.columnDef.header : header.id}`}
                          aria-sort={
                            sort === "asc"
                              ? "ascending"
                              : sort === "desc"
                                ? "descending"
                                : "none"
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {sort === "asc" && (
                            <ChevronUp
                              aria-hidden="true"
                              className="h-3 w-3"
                            />
                          )}
                          {sort === "desc" && (
                            <ChevronDown
                              aria-hidden="true"
                              className="h-3 w-3"
                            />
                          )}
                          {sort === false && (
                            <span aria-hidden="true" className="opacity-30">
                              <ChevronDown className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      ) : header.isPlaceholder ? null : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  className="px-5 py-8 text-center text-sm text-slate-500"
                >
                  {emptyState ?? "No matching rows."}
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={`border-b border-slate-200 last:border-b-0 ${
                    idx % 2 === 1 ? "bg-slate-50/50" : "bg-white"
                  }`}
                  data-testid={
                    rowTestId
                      ? rowTestId(row.original)
                      : testId
                        ? `${testId}-row-${row.id}`
                        : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-5 py-3.5 text-sm text-slate-900"
                    >
                      {cell.column.columnDef.cell
                        ? flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        : (cell.getValue() as RN)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {enablePagination && (
          <div className="flex items-center gap-3 bg-slate-50 px-5 py-3">
            <span className="text-[11px] font-medium text-slate-500">
              Showing {startIdx}–{endIdx} of {total}
            </span>
            <div className="flex-1" />
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Previous page"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 enabled:hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                <ChevronLeft aria-hidden="true" className="h-3 w-3" />
              </button>
              <span className="text-[11px] font-semibold text-slate-700">
                Page {pageIndex + 1} of {totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 enabled:hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                <ChevronRight aria-hidden="true" className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
